export type HCConfig = {
  /**
   * #### Health Center Name
   * @description Used in various places in the app to identify the Health Center in the UI
   */
  appName: string;
  /**
   * #### Health Center Id
   * @description Used to identify the Health Center in the backend when making API requests
   */
  hcId: string;
  /**
   * #### Is Public Health Center
   * @description Used to identify if the Health Center is public or private to enable/disable specific features
   */
  isPublic: boolean;
  /**
   * ### Health Center UF
   * @description Used to identify the Health Center state
   */
  uf: string;

  /**
   * #### Health Center Configuration
   * @description Custom configuration settings for the Health Center to support multiple Health Centers
   */
  config: {
    /**
     * #### Google Analytics Tracking ID
     * @description Used to track user interactions in the app
     */
    trackingId: string;

    /**
     * #### Health Center Domain
     * @description Domain URL Used to identify the Health Center and get its configuration to load the correct settings
     */
    domain: string;

    /**
     * #### Health Center Domain Key
     * @description Key used to get the Health Center Domain URL from environment variables
     */
    domainKey: string;

    /**
     * #### Health Center Domain Key
     * @description Key used to get the Health Center Domain URL from environment variables
     */
    reactDomainKey: string;

    /**
     * #### Health Center URL Parameter
     * @description Used to identify the Health Center in API URL query parameters
     */
    hcParam: string;

    /**
     * #### Client Domain Header
     * @description Used to set the client domain header in API requests
     */
    clientDomainHeader: string;

    /**
     * #### Client Domain
     * @description Used to set the client domain in API requests body
     */
    clientDomain?: string;

    /**
     * #### Support Chat Key
     * @description Used to load the support Voll360 chat widget
     */
    supportChatKey?: string;

    /**
     * #### Gov.br Client ID
     * @description Used to set the client ID when redirecting users to log in with gov.br accounts and to exchange the authorization code for an access token in the backend.
     * This is only necessary if the **ENABLE_LOGIN_WITH_GOV** feature is enabled.
     */
    govBrClientId?: string;
  };

  /**
   * #### Health Center Assets
   * @description Paths to the assets used in the app for custom styling
   */
  assets: {
    /**
     * #### Main Logo Path
     * @description Main logo image used across the application
     */
    logo: string;
    /**
     * #### Upload External Files Logo Path
     * @description Logo image used in the upload external files component
     */
    uploadExternalFilesLogo: string;
    /**
     * #### Preloader Logo Path
     * @description Logo image used in the page preloader
     */
    preloaderLogo: string;
    /**
     * #### Favicon Path
     * @description Favicon image used in the browser tab
     */
    favicon: string;
  };

  /**
   * #### Health Center Links
   * @description References to the Health Center's website, app store pages and legal documents
   */
  links: {
    /**
     * #### Health Center Website URL
     * @description URL to the Health Center's main website
     */
    site: string;
    /**
     * #### Terms of Use URL
     * @description URL to the Health Center's Terms of Use document
     */
    termsOfUse: string;
    /**
     * #### Privacy Policy URL
     * @description URL to the Health Center's Privacy Policy document
     */
    policyPrivacy: string;
    /**
     * #### Play Store URL
     * @description URL to the Health Center's app on the Google Play Store
     */
    playstore: string;
    /**
     * #### App Store URL
     * @description URL to the Health Center's app on the Apple App Store
     */
    appstore: string;
  };

  /**
   * #### Data Deletion Configuration
   * @description Settings related to the data deletion feature
   */
  dataDeletion: {
    /**
     * #### Health Center Name
     * @description Displays the name of the Health Center in the feature UI
     */
    name: string;
    /**
     * #### Health Center Slug
     * @description Used in the feature URLs and API requests
     */
    slug: string;
    /**
     * #### Agreement Terms URL
     * @description URL to the data deletion agreement terms document
     */
    agreementTermsUrl: string;
    /**
     * #### Header Background
     * @description CSS `background` value (color or gradient) used behind the Data Deletion header logo. Shared markup/CSS lives in data-deletion-layout.component; only this value and the logo below should change per Health Center.
     */
    background: string;
    /**
     * #### Header Logo
     * @description Logo displayed in the Data Deletion feature header
     */
    logo: {
      src: string;
      width: number;
      height: number;
    };
  };

  firstAccess: {
    /**
     * #### Health Center Name
     * @description Displays the name of the Health Center in the feature UI
     */
    name: string;

    /**
     * #### Health Center City
     * @description Displays the city of the Health Center in the feature UI
     */
    city?: string;
    /**
     * #### Health Center Slug
     * @description Used to know when the show more content data should be loaded
     */
    slug: string;
    /**
     * #### Health Center Abbreviation
     * @description Used to know when the first access group field should be configured
     */
    abbreviation: string;
    /**
     * #### Health Center Email Domain
     * @description Used to show the email domain when creating a new patient user
     */
    emailDomain?: string;
    /**
     * #### Header Background
     * @description CSS `background` value (color or gradient) used behind the First Access header logo. Shared markup/CSS lives in authorization-layout.component; only this value and the logo below should change per Health Center.
     */
    background: string;
    /**
     * #### Health Center Logo
     * @description Logo used in the feature UI
     */
    logo: {
      src: string;
      width: number;
      height: number;
    };
  };

  /**
   * #### Meal Plan Configuration
   * @description Settings related to the meal plan document feature
   */
  mealPlan: {
    /**
     * #### Enable Send Files
     * @description Enables doctor to send files functionality in the meal plan document feature
     */
    enableSendFiles?: boolean;
    /**
     * #### Enable Document Creation
     * @description Enables doctor to create documents functionality in the meal plan document feature
     */
    enableDocumentCreation?: boolean;
    /**
     * #### Document Types Configuration
     * @description Settings to show/hide specific document types in the meal plan document feature
     */
    documentTypes?: {
      /**
       * #### Show Meal Plan Document Type
       * @description Show/hide meal plan document type
       */
      showMealPlan?: boolean;
      /**
       * #### Show Therapeutic Plan Document Type
       * @description Show/hide therapeutic plan document type
       */
      showTherapeuticPlan?: boolean;
      /**
       * #### Show Nursing Plan Document Type
       * @description Show/hide nursing plan document type
       */
      showNursingPlan?: boolean;
    };
  };

  /**
   * #### Application Routes
   * @description Custom routes used in the application for specific features
   */
  routes: {
    /**
     * #### First Access Route
     * @description Route used for the first access feature
     */
    firstAccess: string;
  };

  /**
   * #### HC Enabled Features
   * @description List of features enabled for the Health Center
   */
  features: HCConfigFeatures[];

  /**
   * #### HC Applied Rules
   * @description List of rules applied for the Health Center
   */
  rules: HCConfigRules[];
};

/**
 * #### Health Center Asset Path
 * @description Builds the relative asset path used by the app for Health Center static files
 */
const hcAssetPath = (hc: string, fileName: string): string => `./assets/hc/${hc}/${fileName}`;

/**
 * #### Health Center Asset Helpers
 * @description Helpers used to keep Health Center asset paths consistent across local configs and Firestore imports
 */
export const HCConfigAssets = {
  /**
   * #### Main Logo Path
   * @description Builds the path for the main logo used across the application
   */
  mainLogo(hc: string, fileName = 'main-logo.png'): string {
    return hcAssetPath(hc, fileName);
  },
  /**
   * #### Upload External Files Logo Path
   * @description Builds the path for the logo used in the upload external files component
   */
  uploadExternalFilesLogo(hc: string, fileName = 'upload-external-files-logo.png'): string {
    return hcAssetPath(hc, fileName);
  },
  /**
   * #### Preloader Logo Path
   * @description Builds the path for the logo used in the page preloader
   */
  preloaderLogo(hc: string, fileName = 'preloader-logo.png'): string {
    return hcAssetPath(hc, fileName);
  },
  /**
   * #### Favicon Path
   * @description Builds the path for the icon used in the browser tab
   */
  favicon(hc: string, fileName = 'favicon.png'): string {
    return hcAssetPath(hc, fileName);
  },
  /**
   * #### First Access Logo Path
   * @description Builds the path for the logo used in the First Access feature UI
   */
  firstAccessLogo(hc: string, fileName = 'first-access-logo.png'): string {
    return hcAssetPath(hc, fileName);
  },
  /**
   * #### Data Deletion Logo Path
   * @description Builds the path for the logo used in the Data Deletion feature UI
   */
  dataDeletionLogo(hc: string, fileName = 'data-deletion-logo.png'): string {
    return hcAssetPath(hc, fileName);
  },
};

export enum HCConfigFeatures {
  /**
   * #### FIRST_ACCESS_REGISTER
   * @description Enables the functionality for patients to register themselves through the first access page
   */
  FIRST_ACCESS_REGISTER = 'first-access-register',
  /**
   * #### FIRST_ACCESS_SEE_MORE_CONTENT
   * @description Enables the functionality for patients to see more content in the first access page
   */
  FIRST_ACCESS_SEE_MORE_CONTENT = 'first-access-see-more-content',
  /**
   * #### REFERRAL_GUIDE
   * @description Enables the functionality for doctors to create and manage REFERRAL_GUIDE documents
   */
  REFERRAL_GUIDE = 'referral-guide',
  /**
   * #### REQUEST_ATTENDANCE
   * @description Enables the functionality to show a selector used by the patient to choose the type of attendance (teleconsultation or in-person)
   */
  REQUEST_ATTENDANCE = 'request-attendance',
  /**
   * #### HC_ADM_CAN_CHANGE_PATIENT_PASSWORD
   * @description Enables the functionality for HC Admin users to change patient passwords
   */
  HC_ADM_CAN_CHANGE_PATIENT_PASSWORD = 'hc-adm-can-change-patient-password',

  /**
   * ### PATIENT_INVITE_AND_EDIT_FORM_REQUIRE_CPF
   * @description Toggle the visibility of the External ID field in the patient invite and edit form
   * */
  PATIENT_INVITE_AND_EDIT_FORM_INCLUDE_EXTERNAL_ID = 'patient-invite-edit-include-external-id',

  /**
   * ### PATIENT_INVITE_AND_EDIT_FORM_INCLUDE_CNS
   * @description Toggle the visibility of the CNS field in the patient invite and edit form
   * */
  PATIENT_INVITE_AND_EDIT_FORM_INCLUDE_CNS = 'patient-invite-edit-include-cns',

  /**
   * #### ENABLE_FIRST_ACCESS
   * @description Enables the First Access feature
   */
  ENABLE_FIRST_ACCESS = 'enable-first-access',

  /**
   * #### PATIENT_COMPLEMENTARY_DATA_FORM_INCLUDE_PHONE
   * @description Includes the phone field in the patient complementary data form
   */
  PATIENT_COMPLEMENTARY_DATA_FORM_INCLUDE_PHONE = 'patient-complementary-data-form-include-phone',

  /**
   * #### MANCHESTER_QUESTIONNAIRE
   * @description Enables the Manchester Questionnaire for patient triage
   */
  MANCHESTER_QUESTIONNAIRE = 'manchester-questionnaire',

  /**
   * #### OPTIONAL_QUESTIONNAIRE_FOR_SECOND_OPINION
   * @description Stops requiring the requesting professional to answer the triage questionnaire
   * (Questionnaire/actual) when submitting a second-opinion request (after selecting one of the
   * teleconsultoria/teleinterconsulta modalities offered on the second-opinion eligibility check). The
   * questionnaire is still fetched and displayed, and remains required for regular requests (including
   * when the patient declines the second-opinion modalities).
   */
  OPTIONAL_QUESTIONNAIRE_FOR_SECOND_OPINION = 'optional-questionnaire-for-second-opinion',

  /**
   * #### ENABLE_LOGIN_WITH_GOV
   * @description Enables the functionality for user to log in with gov.br accounts
   */
  ENABLE_LOGIN_WITH_GOV = 'enable-login-with-gov',

  /**
   * #### BULK_DEACTIVATION_BENEFICIARIES
   * @description Handles the bulk deactivation of beneficiaries through CSV upload
   */
  BULK_DEACTIVATION_BENEFICIARIES = 'bulk-deactivation-beneficiaries',

  /**   * #### PROCEDURES_APAC_SADT
   * @description Enables the functionality to doctor send procedures through APAC and SADT documents
   */

  PROCEDURES_APAC_SADT = 'procedures-apac-sadt',

  /**
   * #### VIOLENCE_VICTIM_IDENTIFICATION
   * @description Enables the functionality to identify if the patient has been a victim of violence during the attendance.
   */
  VIOLENCE_VICTIM_IDENTIFICATION = 'violence-victim-identification',

  /**
   * #### PATIENT_COMPLEMENTARY_DATA_FORM_INCLUDE_ATYPICAL_CHILDREN_GUARDIAN
   * @description Includes the atypical children guardian field in the patient complementary data form, allowing patients to declare if they are guardians of atypical children.
   */
  PATIENT_COMPLEMENTARY_DATA_FORM_INCLUDE_ATYPICAL_CHILDREN_GUARDIAN = 'patient-complementary-data-form-include-atypical-children-guardian',

  /**
   * #### E_SUS_PROFESSIONAL_CLINICAL_HISTORY
   * @description Enables access to the e-SUS Professional clinical history.
   * This feature allows doctors to view the patient's longitudinal record,
   * including previous attendances, medications, and exams to support clinical decisions.
   */
  E_SUS_PROFESSIONAL_CLINICAL_HISTORY = 'e-sus-professional-clinical-history',

  /**
   * #### PRISON_SYSTEM_PATIENT_IDENTIFICATION
   * @description Enables the functionality to identify if the patient is being attended through the prison system during the attendance.
   */
  PRISON_SYSTEM_PATIENT_IDENTIFICATION = 'prison-system-patient-identification',

  /**
   * #### AGORA_SPEECH_TO_TEXT
   * @description Enables the Agora Speech-to-Text (STT) transcription during teleconsultation video calls
   */
  AGORA_SPEECH_TO_TEXT = 'agora-speech-to-text',

  /**
   * #### CAMERA_MIC_PREVIEW_BEFORE_CALL_JOIN
   * @description Enables the camera/microphone preview screen shown before joining a teleconsultation video call
   */
  CAMERA_MIC_PREVIEW_BEFORE_CALL_JOIN = 'camera-mic-preview-before-call-join',

  /**
   * #### BLOCK_MOBILE_WEB_LOGIN
   * @description Redirects the login page opened in a mobile browser (outside the app WebView) to the app download screen (Play Store/App Store), blocking web login on mobile
   */
  BLOCK_MOBILE_WEB_LOGIN = 'block-mobile-web-login',
}

export enum HCConfigRules {
  /**
   * #### PATIENT_INVITE_AND_EDIT_FORM_REQUIRE_CPF_OR_EMAIL
   * @description Require CPF or Email to be filled in the patient invite and edit form
   */
  PATIENT_INVITE_AND_EDIT_FORM_REQUIRE_CPF_OR_EMAIL = 'patient-invite-edit-require-cpf-or-email',

  /**
   * #### PATIENT_INVITE_AND_EDIT_FORM_REQUIRE_CPF_OR_EMAIL_OR_CNS
   * @description The patient registry must contain at least one of the pre-enclosed fields CPF, Email or CNS
   */
  PATIENT_INVITE_AND_EDIT_FORM_REQUIRE_CPF_OR_EMAIL_OR_CNS = 'patient-invite-edit-require-cpf-or-email-or-cns',

  /**
   * #### PATIENT_INVITE_AND_EDIT_FORM_REQUIRE_CPF
   * @description Require CPF field to be filled in the patient invite and edit form
   */
  PATIENT_INVITE_AND_EDIT_FORM_REQUIRE_CPF = 'patient-invite-edit-require-cpf',

  /**
   * #### PATIENT_INVITE_AND_EDIT_FORM_REQUIRE_EXTERNAL_ID
   * @description Require External Id field to be filled in the patient invite and edit form
   */
  PATIENT_INVITE_AND_EDIT_FORM_REQUIRE_EXTERNAL_ID = 'patient-invite-edit-require-external-id',

  /**
   * #### PATIENT_INVITE_AND_EDIT_FORM_REQUIRE_CNS
   * @description Require CNS field to be filled in the patient invite and edit form
   */
  PATIENT_INVITE_AND_EDIT_FORM_REQUIRE_CNS = 'patient-invite-edit-require-cns',

  /**
   * #### PATIENT_INVITE_AND_EDIT_FORM_REQUIRE_EMAIL
   * @description Require Email field to be filled in the patient invite and edit form
   */
  PATIENT_INVITE_AND_EDIT_FORM_REQUIRE_EMAIL = 'patient-invite-edit-require-email',

  /**
   * #### APPOINTMENT_MANAGEMENT
   * @description Enables the appointment management page
   */
  APPOINTMENT_MANAGEMENT = 'appointment-management',

  /**
   * #### PATIENT_COMPLEMENTARY_DATA_FORM_REQUIRE_PHONE
   * @description Forces the phone field to be filled during patient complementary data form submission
   */
  PATIENT_COMPLEMENTARY_DATA_FORM_REQUIRE_PHONE = 'patient-complementary-data-form-require-phone',

  /**
   * #### PATIENT_COMPLEMENTARY_DATA_FORM_DISABLE_CPF
   * @description Disables the CPF field in the patient complementary data form
   */
  PATIENT_COMPLEMENTARY_DATA_FORM_DISABLE_CPF = 'patient-complementary-data-form-disable-cpf',

  /**
   * #### IN CASES WHERE HCS HAS HEALTHY UNITS REGISTERED WITH CNES, THIS RULE CHECKS THE EXISTANCE OF REMOTE GROUPS.
   * @description Ability to check the existence of remote groups in health centers that have healthy units registered with CNES.
   */
  HC_HAS_CNES = 'hc-has-cnes',

  /**
   * #### BLOCK_REGISTRATION_WITH_OUT_OF_STATE_ZIPCODE
   * @description Blocks ZIP codes from states different from the Health Center's state (UF).
   */
  BLOCK_REGISTRATION_WITH_OUT_OF_STATE_ZIPCODE = 'block-registration-with-out-of-state-zipcode',

  /**
   * #### PHYSICIAN_SIGNATURE_VALIDATION
   * @description Requires doctors to validate their physician signature through the embedded validation modal
   * before using the Health Center, on every login/refresh. When the doctor has no digital certificate
   * registered in Vidaas, sends them to the certificate registration instructions instead of the QR Code
   * validation modal, and logs them out if they cancel or the check fails, since they can't be allowed into
   * the workspace without a certificate.
   */
  PHYSICIAN_SIGNATURE_VALIDATION = 'physician-signature-validation',

  /**
   * #### PHYSICIAN_SIGNATURE_STATUS_WIDGET
   * @description Shows the physician signature status widget (with a renew/validate/create action button)
   * in the specialist sessions workspace, letting doctors manage their digital signature on demand instead
   * of being forced through it at login.
   */
  PHYSICIAN_SIGNATURE_STATUS_WIDGET = 'physician-signature-status-widget',

  /**
   * #### DOCTORS_LIST_SHOW_DIGITAL_SIGNATURE_STATUS
   * @description Shows the digital certificate (Vidaas) and digital signature validation status columns
   * in the HC admin doctors list, letting operators see at a glance which doctors have a Vidaas certificate
   * registered and whether their digital signature is currently validated.
   */
  DOCTORS_LIST_SHOW_DIGITAL_SIGNATURE_STATUS = 'doctors-list-show-digital-signature-status',
}
